import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Activity, ArrowLeft, BarChart3, Database, Globe, HardDrive,
  KeyRound, Loader2, Mail, RefreshCw, Search, Server, ShieldCheck, TriangleAlert,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const errMsg = (e, fallback) => e?.response?.data?.detail || fallback;
const fmtInt = (v) => (Number(v) || 0).toLocaleString("en-AU");
const fmtBytes = (bytes) => {
  const b = Number(bytes) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
};
const fmtEpoch = (v) => {
  const n = Number(v);
  if (!n) return "—";
  return new Date(n * 1000).toLocaleDateString("en-AU", {
    day: "2-digit", month: "short", year: "numeric",
  });
};

function StatCard({ label, value, sub }) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
        <div className="text-sm font-bold truncate">{value ?? "—"}</div>
        {sub && <div className="text-[10px] text-zinc-400 truncate">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function SectionCard({ icon: Icon, title, error, children, count }) {
  return (
    <Card>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-zinc-400" />
          <div className="font-semibold text-sm">{title}</div>
          {count != null && <Badge variant="outline" className="text-[10px]">{count}</Badge>}
        </div>
        {error ? (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-1.5">
            <TriangleAlert className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>Not available: {error}</span>
          </div>
        ) : children}
      </CardContent>
    </Card>
  );
}

/* ── AWStats traffic panel (per mapped domain) ─────────────────── */
function AwstatsPanel({ mapping, properties, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);
  const [saving, setSaving] = useState(false);
  const [pulling, setPulling] = useState(false);

  const loadData = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/awstats/mappings/${mapping.mapping_id}/data`, { params: { days } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => toast.error(errMsg(e, "Failed to load AWStats data")))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [mapping.mapping_id, days]);

  useEffect(() => {
    const cleanup = loadData();
    return cleanup;
  }, [loadData]);

  const updateMapping = async (patch) => {
    setSaving(true);
    try {
      await api.put(`/awstats/mappings/${mapping.mapping_id}`, patch);
      toast.success("Mapping saved");
      onChanged && onChanged();
    } catch (e) { toast.error(errMsg(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const pullNow = async () => {
    setPulling(true);
    try {
      const { data: r } = await api.post(`/awstats/pull/${mapping.mapping_id}`);
      if (r.ok) {
        if (r.note) {
          toast.warning(r.note, { duration: 12000 });
        } else if (r.daily_rows === 0 && r.monthly_rows === 0) {
          toast.warning(
            `No AWStats rows found for ${r.domain} across the last 12 months. ` +
            `Check that AWStats is enabled in cPanel → Metrics → Choose Log Programs.`,
            { duration: 12000 });
        } else {
          toast.success(`Pulled ${r.daily_rows} daily + ${r.monthly_rows} monthly rows for ${r.domain}`);
        }
        loadData();
        onChanged && onChanged();
      } else {
        toast.error(r.error || "Pull failed");
      }
    } catch (e) { toast.error(errMsg(e, "Pull failed")); }
    finally { setPulling(false); }
  };

  const chartData = useMemo(() =>
    (data?.rows || []).map((r) => ({
      date: r.date.slice(5),
      visits: r.visits,
      unique: r.unique_visitors,
      pages: r.pages,
    })), [data]);

  const isMapped = !!mapping.business_unit_id;

  return (
    <div className="rounded-md border border-zinc-200 bg-zinc-50/50 p-3 space-y-3"
      data-testid={`account-awstats-panel-${mapping.mapping_id}`}>
      <div className="flex items-center gap-2 text-xs flex-wrap">
        <span className="font-semibold">{mapping.domain}</span>
        <Badge variant="outline" className="text-[10px]">{mapping.domain_type || "domain"}</Badge>
        {mapping.last_error ? (
          <Badge className="bg-red-50 text-red-700 border-red-200 text-[10px]" variant="outline"
            title={mapping.last_error}>error</Badge>
        ) : mapping.enabled && isMapped ? (
          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]" variant="outline">syncing nightly</Badge>
        ) : isMapped ? (
          <Badge variant="outline" className="text-[10px]">mapped, paused</Badge>
        ) : (
          <Badge variant="outline" className="text-zinc-400 text-[10px]">unmapped</Badge>
        )}
      </div>

      {/* Inline mapping controls */}
      <div className="flex flex-wrap items-center gap-2 text-xs bg-white rounded border border-zinc-200 p-2">
        <span className="text-zinc-500 shrink-0">Map to property:</span>
        <Select
          value={mapping.business_unit_id || "__none__"}
          disabled={saving}
          onValueChange={(v) => updateMapping({ business_unit_id: v === "__none__" ? "" : v })}>
          <SelectTrigger className="h-8 text-xs w-full max-w-[300px]"
            data-testid={`account-awstats-bu-${mapping.mapping_id}`}>
            <SelectValue placeholder="— unmapped —" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— unmapped —</SelectItem>
            {properties.map((b) => (
              <SelectItem key={b.business_unit_id} value={b.business_unit_id}>
                {b.name}
                {b.client_name && <span className="text-zinc-400"> · {b.client_name}</span>}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isMapped && (
          <>
            <Button variant="ghost" size="sm" className="h-7 text-xs"
              onClick={() => updateMapping({ enabled: !mapping.enabled })}
              disabled={saving}
              data-testid={`account-awstats-toggle-${mapping.mapping_id}`}>
              {mapping.enabled ? "Pause" : "Enable"}
            </Button>
            <Button variant="outline" size="sm" className="h-7 text-xs"
              onClick={pullNow} disabled={pulling}
              data-testid={`account-awstats-pull-${mapping.mapping_id}`}>
              {pulling
                ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                : <BarChart3 className="h-3 w-3 mr-1" />}
              Pull now
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs text-zinc-500"
              onClick={async () => {
                try {
                  const { data: r } = await api.post(
                    `/awstats/mappings/${mapping.mapping_id}/reparse-uniques`);
                  toast.success(
                    `Re-parsed ${r.rows_checked} monthly row${r.rows_checked === 1 ? "" : "s"} · ${r.rows_updated} updated`);
                  loadData();
                } catch (e) { toast.error(errMsg(e, "Reparse failed")); }
              }}
              data-testid={`account-awstats-reparse-${mapping.mapping_id}`}>
              Re-parse uniques
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs text-zinc-500"
              onClick={async () => {
                try {
                  const { data: r } = await api.post(
                    `/awstats/mappings/${mapping.mapping_id}/pull-uniques-fallback`,
                    null, { params: { months: 6 } });
                  toast.success(
                    `Fallback: probed ${r.months_probed} months · ${r.months_recovered} unique-visitor totals recovered`);
                  loadData();
                } catch (e) { toast.error(errMsg(e, "Fallback fetch failed")); }
              }}
              data-testid={`account-awstats-fallback-${mapping.mapping_id}`}>
              Fetch uniques (fallback)
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs text-zinc-500"
              onClick={async () => {
                try {
                  const { data: dbg } = await api.get(
                    `/awstats/mappings/${mapping.mapping_id}/debug`);
                  const w = window.open("", "_blank");
                  if (w) {
                    w.document.write(`<pre style="font-family:monospace;padding:20px;font-size:11px;white-space:pre-wrap">${
                      JSON.stringify(dbg, null, 2).replace(/</g, "&lt;")
                    }</pre>`);
                    w.document.title = `AWStats debug · ${mapping.domain}`;
                  }
                } catch (e) { toast.error(errMsg(e, "Debug fetch failed")); }
              }}
              data-testid={`account-awstats-debug-${mapping.mapping_id}`}>
              Debug
            </Button>
          </>
        )}
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="h-7 w-32 text-xs ml-auto"
            data-testid={`account-awstats-days-${mapping.mapping_id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="180">Last 6 months</SelectItem>
            <SelectItem value="365">Last 12 months</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {mapping.last_error && (
        <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded p-2 flex items-start gap-1.5">
          <TriangleAlert className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>Last pull error: {mapping.last_error}</span>
        </div>
      )}

      {loading ? (
        <div className="text-xs text-zinc-500 flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />Loading traffic…
        </div>
      ) : (data?.rows || []).length === 0 ? (
        <div className="text-xs text-zinc-500 py-3 text-center">
          {isMapped
            ? <>No AWStats rows stored yet — click <b>Pull now</b> above to backfill.</>
            : <>Map this domain to a property first, then click <b>Pull now</b>.</>}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
            <div className="bg-white rounded border border-zinc-200 p-2">
              <div className="text-[10px] uppercase tracking-wide text-zinc-500">Unique visitors</div>
              <div className="text-sm font-bold">{fmtInt(data.totals.unique_visitors)}</div>
            </div>
            <div className="bg-white rounded border border-zinc-200 p-2">
              <div className="text-[10px] uppercase tracking-wide text-zinc-500">Visits</div>
              <div className="text-sm font-bold">{fmtInt(data.totals.visits)}</div>
            </div>
            <div className="bg-white rounded border border-zinc-200 p-2">
              <div className="text-[10px] uppercase tracking-wide text-zinc-500">Pages</div>
              <div className="text-sm font-bold">{fmtInt(data.totals.pages)}</div>
            </div>
            <div className="bg-white rounded border border-zinc-200 p-2">
              <div className="text-[10px] uppercase tracking-wide text-zinc-500">Hits</div>
              <div className="text-sm font-bold">{fmtInt(data.totals.hits)}</div>
            </div>
            <div className="bg-white rounded border border-zinc-200 p-2">
              <div className="text-[10px] uppercase tracking-wide text-zinc-500">Bandwidth</div>
              <div className="text-sm font-bold">{fmtBytes(data.totals.bandwidth)}</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={190}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={fmtInt} />
              <Tooltip formatter={(v) => fmtInt(v)} />
              <Line type="monotone" dataKey="visits" name="Visits" stroke="#0ea5e9" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="unique" name="Unique" stroke="#22c55e" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="pages" name="Pages" stroke="#a855f7" strokeWidth={1.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
}

/* ── Page ───────────────────────────────────────────────────────── */
export default function HostAccountDetailPage() {
  const { serverId, whmUser } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Back-nav honours the page that opened us — the per-client Hosting
  // tab passes {state:{from,label}} so users return to their client
  // list, not the global Host Servers page.
  const backTo = location.state?.from || "/host-servers";
  const backLabel = location.state?.backLabel || "Hosting";
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [properties, setProperties] = useState([]);
  const [discovering, setDiscovering] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [detailsRes, propsRes] = await Promise.all([
        api.get(`/hosting/servers/${serverId}/accounts/${whmUser}/details`),
        api.get(`/awstats/property-picker`).catch(() => ({ data: { properties: [] } })),
      ]);
      setD(detailsRes.data);
      setProperties(propsRes.data.properties || []);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "Unknown error";
      setLoadError({ status, detail });
      toast.error(errMsg(e, "Could not load account details"));
    }
    finally { setLoading(false); }
  }, [serverId, whmUser]);

  useEffect(() => { load(); }, [load]);

  const discoverNow = async () => {
    setDiscovering(true);
    try {
      const { data } = await api.post(`/awstats/discover/${serverId}/${whmUser}`);
      toast.success(`Found ${data.domains_found} domain(s) · ${data.newly_added} new`);
      await load();
    } catch (e) { toast.error(errMsg(e, "Discovery failed")); }
    finally { setDiscovering(false); }
  };

  const sum = d?.summary?.error ? {} : (d?.summary || {});
  const sectionErrors = useMemo(() => {
    if (!d) return [];
    const keys = ["summary", "domains", "email_accounts", "databases",
                  "ftp_accounts", "quota", "ssl", "bandwidth"];
    return keys
      .filter((k) => d[k] && typeof d[k] === "object" && d[k].error)
      .map((k) => ({ section: k, error: d[k].error }));
  }, [d]);

  const allDomains = useMemo(() => {
    const dom = d?.domains;
    if (!dom || dom.error) return [];
    const rows = [];
    if (dom.main_domain) rows.push({ domain: dom.main_domain, type: "main" });
    (dom.addon_domains || []).forEach((x) => rows.push({ domain: x, type: "addon" }));
    (dom.sub_domains || []).forEach((x) => rows.push({ domain: x, type: "subdomain" }));
    (dom.parked_domains || []).forEach((x) => rows.push({ domain: x, type: "parked/alias" }));
    return rows;
  }, [d]);

  if (loading && !d) {
    return (
      <div className="p-6 text-sm text-zinc-500 flex items-center gap-2" data-testid="account-detail-loading">
        <Loader2 className="h-4 w-4 animate-spin" />Loading hosting account…
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4 max-w-6xl" data-testid="host-account-detail-page">
      {/* Header */}
      <div className="flex items-start gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => navigate(backTo)}
          data-testid="account-detail-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" />{backLabel}
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2 flex-wrap">
            <Globe className="h-6 w-6 text-sky-600 shrink-0" />
            <span className="truncate" data-testid="account-detail-domain">{sum.domain || whmUser}</span>
            {sum.suspended
              ? <Badge className="bg-red-50 text-red-700 border-red-200" variant="outline">suspended</Badge>
              : sum.domain
                ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200" variant="outline">active</Badge>
                : null}
          </h1>
          <div className="text-sm text-zinc-500 mt-1 flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs">cPanel user: {whmUser}</span>
            {d?.server && (
              <span className="flex items-center gap-1">
                <Server className="h-3.5 w-3.5" />{d.server.name} · <span className="font-mono text-xs">{d.server.host}</span>
              </span>
            )}
            {d?.client && (
              <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 text-[10px]"
                data-testid="account-detail-client-badge">
                <ShieldCheck className="h-3 w-3 mr-1" />{d.client.company_name}
              </Badge>
            )}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}
          data-testid="account-detail-refresh-btn">
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />Refresh
        </Button>
        <Button variant="outline" size="sm"
          onClick={async () => {
            try {
              const { data } = await api.get(
                `/hosting/servers/${serverId}/accounts/${whmUser}/debug`);
              const w = window.open("", "_blank");
              if (w) {
                w.document.write(`<pre style="font-family:monospace;padding:20px;font-size:12px;white-space:pre-wrap">${
                  JSON.stringify(data, null, 2).replace(/</g, "&lt;")
                }</pre>`);
                w.document.title = `UAPI debug · ${whmUser}`;
              }
            } catch (e) { toast.error(errMsg(e, "Debug fetch failed")); }
          }}
          data-testid="account-detail-debug-btn">
          Debug UAPI
        </Button>
      </div>

      {/* Diagnostic: top-level fetch error */}
      {loadError && (
        <div className="rounded border border-red-300 bg-red-50 p-3 text-sm space-y-1"
          data-testid="account-detail-fetch-error">
          <div className="flex items-center gap-2 font-semibold text-red-800">
            <TriangleAlert className="h-4 w-4" />
            Could not load this account
            {loadError.status && <Badge variant="outline" className="text-[10px]">HTTP {loadError.status}</Badge>}
          </div>
          <div className="text-red-700 text-xs break-words">{loadError.detail}</div>
          <div className="text-red-600/80 text-[11px]">
            Common causes: cPanel user <b>{whmUser}</b> does not exist on this WHM,
            the WHM API token lost privileges, or the server is unreachable.
          </div>
        </div>
      )}

      {/* Diagnostic: per-section errors */}
      {sectionErrors.length > 0 && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-xs space-y-1"
          data-testid="account-detail-section-errors">
          <div className="flex items-center gap-2 font-semibold text-amber-900">
            <TriangleAlert className="h-4 w-4" />
            {sectionErrors.length} section(s) failed to load
          </div>
          <ul className="list-disc ml-5 text-amber-800">
            {sectionErrors.map((s) => (
              <li key={s.section}><b>{s.section}:</b> {s.error}</li>
            ))}
          </ul>
        </div>
      )}

      {d?.summary?.error && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
          Account summary unavailable: {d.summary.error}
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="account-detail-stats">
        <StatCard label="Plan" value={sum.plan} />
        <StatCard label="IP address" value={sum.ip} />
        <StatCard label="Disk" value={sum.diskused} sub={`limit ${sum.disklimit || "—"}`} />
        <StatCard label="Created" value={sum.startdate} />
        <StatCard label="Contact email" value={sum.email} />
        <StatCard label="Bandwidth (this month)"
          value={d?.bandwidth?.error ? "—" : fmtBytes(d?.bandwidth?.totalbytes)}
          sub={d?.bandwidth?.limit ? `limit ${d.bandwidth.limit}` : undefined} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Domains */}
        <SectionCard icon={Globe} title="Domains on this account"
          error={d?.domains?.error} count={allDomains.length}>
          <div className="max-h-64 overflow-y-auto rounded border">
            <table className="w-full text-sm" data-testid="account-domains-table">
              <tbody>
                {allDomains.map((r) => (
                  <tr key={`${r.type}-${r.domain}`} className="border-t first:border-t-0">
                    <td className="p-2 font-medium">{r.domain}</td>
                    <td className="p-2 text-right">
                      <Badge variant="outline" className="text-[10px]">{r.type}</Badge>
                    </td>
                  </tr>
                ))}
                {!allDomains.length && (
                  <tr><td className="p-4 text-center text-zinc-400 text-xs">No domains returned.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </SectionCard>

        {/* Email accounts */}
        <SectionCard icon={Mail} title="Email accounts"
          error={d?.email_accounts?.error}
          count={Array.isArray(d?.email_accounts) ? d.email_accounts.length : undefined}>
          <div className="max-h-64 overflow-y-auto rounded border">
            <table className="w-full text-sm" data-testid="account-emails-table">
              <tbody>
                {(Array.isArray(d?.email_accounts) ? d.email_accounts : []).map((e) => (
                  <tr key={e.email} className="border-t first:border-t-0">
                    <td className="p-2 font-medium truncate max-w-[220px]">{e.email}</td>
                    <td className="p-2 text-xs text-zinc-500">{e.disk_used || "—"} / {e.disk_quota || "∞"}</td>
                    <td className="p-2 text-right">
                      {e.suspended_login
                        ? <Badge className="bg-red-50 text-red-700 border-red-200 text-[10px]" variant="outline">suspended</Badge>
                        : <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]" variant="outline">active</Badge>}
                    </td>
                  </tr>
                ))}
                {Array.isArray(d?.email_accounts) && !d.email_accounts.length && (
                  <tr><td className="p-4 text-center text-zinc-400 text-xs">No email accounts.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </SectionCard>

        {/* Databases */}
        <SectionCard icon={Database} title="MySQL databases"
          error={d?.databases?.error}
          count={Array.isArray(d?.databases) ? d.databases.length : undefined}>
          <div className="flex flex-wrap gap-1.5" data-testid="account-databases-list">
            {(Array.isArray(d?.databases) ? d.databases : []).map((name) => (
              <Badge key={name} variant="outline" className="font-mono text-[11px]">{name}</Badge>
            ))}
            {Array.isArray(d?.databases) && !d.databases.length && (
              <div className="text-xs text-zinc-400">No databases.</div>
            )}
          </div>
        </SectionCard>

        {/* FTP */}
        <SectionCard icon={KeyRound} title="FTP accounts"
          error={d?.ftp_accounts?.error}
          count={Array.isArray(d?.ftp_accounts) ? d.ftp_accounts.length : undefined}>
          <div className="max-h-64 overflow-y-auto rounded border">
            <table className="w-full text-sm" data-testid="account-ftp-table">
              <tbody>
                {(Array.isArray(d?.ftp_accounts) ? d.ftp_accounts : []).map((f, i) => (
                  <tr key={`${f.user}-${i}`} className="border-t first:border-t-0">
                    <td className="p-2 font-mono text-xs">{f.user}</td>
                    <td className="p-2 text-xs text-zinc-500 truncate max-w-[220px]">{f.dir || "—"}</td>
                    <td className="p-2 text-right text-xs text-zinc-400">{f.type || ""}</td>
                  </tr>
                ))}
                {Array.isArray(d?.ftp_accounts) && !d.ftp_accounts.length && (
                  <tr><td className="p-4 text-center text-zinc-400 text-xs">No FTP accounts.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </SectionCard>

        {/* SSL */}
        <SectionCard icon={ShieldCheck} title="SSL certificates"
          error={d?.ssl?.error}
          count={Array.isArray(d?.ssl) ? d.ssl.length : undefined}>
          <div className="space-y-1.5" data-testid="account-ssl-list">
            {(Array.isArray(d?.ssl) ? d.ssl : []).map((c, i) => {
              const expired = c.not_after && Number(c.not_after) * 1000 < Date.now();
              return (
                <div key={`${c.servername}-${i}`}
                  className={`flex items-center gap-2 rounded border px-2.5 py-1.5 text-xs ${expired ? "border-red-200 bg-red-50/60" : "border-emerald-200 bg-emerald-50/40"}`}>
                  <span className="font-medium truncate flex-1">{c.servername}</span>
                  <span className="text-zinc-500 truncate max-w-[160px]">{c.issuer || (c.is_self_signed ? "self-signed" : "")}</span>
                  <span className={expired ? "text-red-700 font-semibold" : "text-zinc-500"}>
                    {expired ? "expired " : "expires "}{fmtEpoch(c.not_after)}
                  </span>
                </div>
              );
            })}
            {Array.isArray(d?.ssl) && !d.ssl.length && (
              <div className="text-xs text-zinc-400">No installed certificates.</div>
            )}
          </div>
        </SectionCard>

        {/* Quota */}
        <SectionCard icon={HardDrive} title="Disk quota" error={d?.quota?.error}>
          {d?.quota && !d.quota.error && (
            <div className="grid grid-cols-2 gap-2 text-sm" data-testid="account-quota-panel">
              <div className="rounded border p-2">
                <div className="text-[10px] uppercase tracking-wide text-zinc-500">Used</div>
                <div className="font-bold">{d.quota.megabytes_used != null ? `${fmtInt(d.quota.megabytes_used)} MB` : "—"}</div>
              </div>
              <div className="rounded border p-2">
                <div className="text-[10px] uppercase tracking-wide text-zinc-500">Limit</div>
                <div className="font-bold">{Number(d.quota.megabyte_limit) ? `${fmtInt(d.quota.megabyte_limit)} MB` : "unlimited"}</div>
              </div>
              <div className="rounded border p-2">
                <div className="text-[10px] uppercase tracking-wide text-zinc-500">Inodes (files)</div>
                <div className="font-bold">{d.quota.inodes_used != null ? fmtInt(d.quota.inodes_used) : "—"}</div>
              </div>
              <div className="rounded border p-2">
                <div className="text-[10px] uppercase tracking-wide text-zinc-500">Inode limit</div>
                <div className="font-bold">{Number(d.quota.inode_limit) ? fmtInt(d.quota.inode_limit) : "unlimited"}</div>
              </div>
            </div>
          )}
        </SectionCard>
      </div>

      {/* AWStats */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <BarChart3 className="h-4 w-4 text-amber-600" />
            <div className="font-semibold text-sm">AWStats traffic</div>
            <Badge variant="outline" className="text-[10px]">
              {(d?.awstats_mappings || []).length} mapped domain(s)
            </Badge>
            <Button variant="outline" size="sm" className="ml-auto h-8 text-xs"
              onClick={discoverNow} disabled={discovering}
              data-testid="account-awstats-discover-btn">
              {discovering
                ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                : <Search className="h-3.5 w-3.5 mr-1" />}
              Discover domains
            </Button>
          </div>
          <div className="text-[11px] text-zinc-500 -mt-1">
            Workflow: <b>Discover</b> → <b>Map</b> each domain to a Property → <b>Enable</b> → <b>Pull now</b>. Enabled mappings also sync nightly (04:15 Sydney).
          </div>
          {(d?.awstats_mappings || []).length === 0 ? (
            <div className="text-xs text-zinc-500 py-4 text-center" data-testid="account-awstats-empty">
              <Activity className="h-6 w-6 mx-auto mb-1 opacity-40" />
              No AWStats domains discovered for this account yet.<br />
              Click <b>Discover domains</b> above to enumerate them from cPanel.
            </div>
          ) : (
            <div className="space-y-3">
              {(d?.awstats_mappings || []).map((m) => (
                <AwstatsPanel key={m.mapping_id} mapping={m} properties={properties} onChanged={load} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
